import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '../../ui_elements/tooltip/Tooltip';
import Avatar from '../../ui_elements/avatar/Avatar';
import Markdown from '../../base/Markdown';
import { Subtext, H6, P } from '../../base/typography';
import { Indicator } from '../../ui_elements/indicators';
import { ReplyForm, EditForm } from './CommentForms';
import { useRelativeTime } from '../../hooks';
import {
  AuthorName,
  FlexWrapper,
  FlexContent,
  AvatarPresenter,
  ResolveButton,
  EditIcon,
  CommentHeaderContainer,
  CommentBodyContainer,
  CommentFormContainer,
  CommentWrapper,
  CommentListContainer,
  OpeningThreadContainer,
  FlexPresenter,
  HighlightedTextContainer,
  StyledSubtext,
  HighlightedText,
  BodyContainer,
  ArticleIcon,
  IndicatorButton
} from './commentStyle';

export default function Comment(props) {
  const {
    id,
    body,
    highlightedText,
    avatar,
    fullName,
    authorIsOwner,
    formattedCreatedAt,
    authorTooltip,
    authorBadge,
    authorBadgeColor,
    onResolve,
    allowUnresolve,
    onUnresolve,
    onEditSubmit,
    onEditCancel,
    children,
    showChildren,
    updatedAt,
    width,
    showReplyForm,
    resolved,
    borderLeft,
    canBeUpdated,
    canBeResolved,
    users,
    createdAt,
    inStoryText,
    onClick,
    isActive
  } = props;

  const [relativeDatetime, formattedDatetime] = useRelativeTime(formattedCreatedAt);

  const [editMode, setEditMode] = useState(false);
  const [commentResolved, setResolved] = useState(false);
  const [inputVal, setInputVal] = useState(null);

  useEffect(() => {
    setInputVal(body);
  }, [body]);

  useEffect(() => {
    setResolved(resolved);
  }, [resolved]);

  const useRelativeUpdate = updatedAt > createdAt;
  const updated = useRelativeUpdate ? updatedAt : null;
  const [relativeUpdatedAt, formattedUpdatedDatetime] = useRelativeTime(updated);

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleSubmit = (data) => {
    setInputVal(data.edit);
    onEditSubmit(data);
    setEditMode(false);
  };

  const handleCancel = () => {
    onEditCancel();
    setEditMode(false);
  };

  const handleResolved = () => {
    setResolved(true);
    onResolve();
  };

  const handleUnresolved = () => {
    setResolved(false);
    onUnresolve();
  };

  const AnnotationStoryText = () => {
    if (inStoryText) {
      return (
        <HighlightedTextContainer>
          <HighlightedText>{highlightedText}</HighlightedText>
        </HighlightedTextContainer>
      );
    } else {
      return (
        <HighlightedTextContainer>
          <StyledSubtext>Original Selected Text:</StyledSubtext>
          <P style={{ paddingLeft: "10px" }}>{highlightedText}</P>
        </HighlightedTextContainer>
      );
    }
  };

  return (
    <CommentWrapper
      key={`comment-${id}`}
      width={width}
      commentResolved={commentResolved}
      showReplyForm={showReplyForm}
      borderLeft={!isActive && borderLeft}
      onClick={onClick || (() => {})}
      isActive={isActive}
    >
      <OpeningThreadContainer isActive={isActive}>
        <CommentHeaderContainer>
          <FlexPresenter>
            <FlexWrapper>
              <FlexContent style={{ height: '30px' }}>
                <ArticleIcon type="comment" />
                <H6>Comment</H6>
              </FlexContent>
              {!editMode && (
                <FlexContent>
                  {commentResolved ? (
                    <FlexWrapper>
                      <Indicator text="Resolved">Resolved</Indicator>
                      {allowUnresolve && (
                        <IndicatorButton onClick={() => handleUnresolved()} text="Re-Open">
                          Re-Open
                        </IndicatorButton>
                      )}
                    </FlexWrapper>
                  ) : (
                    canBeResolved && (
                      <ResolveButton size="small" onClick={handleResolved}>
                        Resolve
                      </ResolveButton>
                    )
                  )}
                </FlexContent>
              )}
            </FlexWrapper>
          </FlexPresenter>
        </CommentHeaderContainer>
        <CommentBodyContainer>
          <AvatarPresenter>
            <FlexWrapper>
              <FlexContent>
                <Avatar
                  src={avatar}
                  alt={fullName}
                  tooltip={{ text: authorTooltip, left: '15px' }}
                  badgeText={authorBadge}
                  badgeColor={authorBadgeColor}
                  size="large"
                />
                <AuthorName>{fullName}</AuthorName>
              </FlexContent>
              <Subtext style={{ marginLeft: 'auto' }}>
                <Tooltip position="start" content={formattedDatetime}>
                  <span>{relativeDatetime}</span>
                </Tooltip>
                {updatedAt && useRelativeUpdate && (
                  <Tooltip position="start" content={formattedUpdatedDatetime}>
                    <span> • Updated {relativeUpdatedAt} </span>
                  </Tooltip>
                )}
              </Subtext>
              <FlexContent>
                {authorIsOwner && !commentResolved && canBeUpdated && (
                  <EditIcon type="pencil" aria-label="edit" onClick={handleEdit} />
                )}
              </FlexContent>
            </FlexWrapper>
          </AvatarPresenter>
          {highlightedText && !commentResolved && (
            <AnnotationStoryText />
          )}
        </CommentBodyContainer>
        {editMode && (
          <BodyContainer>
            <CommentFormContainer>
              <EditForm
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                body={inputVal}
                users={users}
              />
            </CommentFormContainer>
          </BodyContainer>
        )}
        {!editMode && (
          <BodyContainer>
            <Markdown>{inputVal}</Markdown>
          </BodyContainer>
        )}
      </OpeningThreadContainer>

      {showChildren && children}
    </CommentWrapper>
  );
}

Comment.defaultProps = {
  body: '',
  highlightedText: '',
  authorIsOwner: true,
  authorTooltip: '',
  authorBadge: '',
  authorBadgeColor: '',
  children: null,
  showChildren: true,
  updatedAt: null,
  width: null,
  showReplyForm: undefined,
  // border: false,
  resolved: 0,
  allowUnresolve: false,
  // annotated: false,
  onEditSubmit: () => {},
  onEditCancel: () => {},
  onUnresolve: () => {},
  isActive: false
};

Comment.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  body: PropTypes.string,
  highlightedText: PropTypes.string,
  avatar: PropTypes.string.isRequired,
  fullName: PropTypes.string.isRequired,
  authorIsOwner: PropTypes.bool,
  formattedCreatedAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  authorTooltip: PropTypes.string,
  authorBadge: PropTypes.string,
  authorBadgeColor: PropTypes.string,
  onResolve: PropTypes.func.isRequired,
  allowUnresolve: PropTypes.bool,
  onUnresolve: PropTypes.func,
  onEditSubmit: PropTypes.func,
  onEditCancel: PropTypes.func,
  children: PropTypes.element,
  showChildren: PropTypes.bool,
  updatedAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  width: PropTypes.string,
  showReplyForm: PropTypes.bool,
  // border: PropTypes.bool,
  resolved: PropTypes.number,
  // annotated: PropTypes.bool
  canBeUpdated: PropTypes.bool,
  canBeResolved: PropTypes.bool,
  isAuthorOwner: PropTypes.bool,
  isActive: PropTypes.bool
};

function CommentWithReplyField(props) {
  const {
    onReplySubmit,
    onReplyCancel,
    id,
    showReplyForm,
    borderBottom,
    borderSides,
    border,
    editMode,
    width,
    users,
    borderLeft,
    isActive
  } = props;

  return (
    <CommentListContainer
      width={width}
      border={border}
      showReplyForm={showReplyForm}
      isActive={isActive}
    >
      <Comment {...props} key={id} />
      {showReplyForm && !editMode &&(
        <ReplyForm
          onSubmit={onReplySubmit}
          onCancel={onReplyCancel}
          borderBottom={borderBottom}
          borderSides={borderSides}
          users={users}
          borderLeft={!isActive && borderLeft}
        />
      )}
    </CommentListContainer>
  );
}

const userShape = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  display: PropTypes.string.isRequired,
  avatar: PropTypes.string.isRequired,
};

CommentWithReplyField.defaultProps = {
  showReplyForm: true,
  borderBottom: true,
  borderSides: true,
  border: false,
  editMode: false,
  width: null,
  onClick: undefined,
  users: [],
  isActive: false
};

CommentWithReplyField.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.string
    ])),
    PropTypes.element,
    PropTypes.string,
  ]),
  body: PropTypes.string,
  avatar: PropTypes.string,
  fullName: PropTypes.string,
  formattedCreatedAt: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  authorIsEmployee: PropTypes.bool,
  onResolve: PropTypes.func,
  resolved: PropTypes.number,
  onReplySubmit: PropTypes.func.isRequired,
  onEditSubmit: PropTypes.func.isRequired,
  onReplyCancel: PropTypes.func.isRequired,
  onEditCancel: PropTypes.func,
  onClick: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  showReplyForm: PropTypes.bool,
  borderBottom: PropTypes.bool,
  borderSides: PropTypes.bool,
  border: PropTypes.bool,
  editMode: PropTypes.bool,
  width: PropTypes.string,
  users: PropTypes.arrayOf(PropTypes.shape(userShape)),
  canBeUpdated: PropTypes.bool,
  canBeResolved: PropTypes.bool,
  authorIsOwner: PropTypes.bool,
  isActive: PropTypes.bool
};

export { Comment, CommentWithReplyField };
